import React, { useCallback } from 'react';

import { formatCents } from '@src/common';
import { useAppDispatch, useTranslate } from '@src/core/hooks';

import {
  ProductList,
  ProductListArea,
  ProductListFooter,
  ProductListFooterInfo,
  ProductListHeader,
  ProductListScrollableContent,
} from './styles';
import ProductPackageCard from '../ProductPackageProductCard';
import Chip from '@src/components/reusable/Chip';
import usePackage from '@src/hooks/usePackage';
import { IconCheck } from '@tabler/icons-react';
import { useWidgetId } from '@src/core/IdProvider';
import { ProductPackageProduct } from '@src/core/buyerApiClient';
import { ProductPackageStatus, addNextProductPackageToAdderState } from '@src/features/product';

interface ProductPackageAdderProductListProps {
  productPackageProducts: ProductPackageProduct[];
  status: ProductPackageStatus;
}

const ProductPackageAdderProductList = (props: Readonly<ProductPackageAdderProductListProps>) => {
  const { productPackageProducts, status } = props;

  const widgetId = useWidgetId();

  const { i18n } = useTranslate();
  const dispatch = useAppDispatch();

  const {
    isProductFilled,
    getSelectedDate,
    getSelectedTime,
    getSelectedTariffs,
    selectedProductId,
    countFilledProducts,
    shouldProductBeDisabled,
    packageTotalCents,
    hasInactiveProduct,
  } = usePackage();

  const handleSelectProduct = useCallback(
    (productId: string) => {
      dispatch(addNextProductPackageToAdderState(widgetId, productId));
    },
    [dispatch],
  );

  return (
    <ProductListArea>
      <ProductListScrollableContent>
        <ProductListHeader>
          <p>{i18n('product_package_adder_product_list_title')}</p>

          <Chip
            color={status === 'completed' ? 'success' : 'primary'}
            label={
              <>
                {countFilledProducts}/{productPackageProducts.length}
                {status === 'completed' ? <IconCheck size={16} /> : null}
              </>
            }
          />
        </ProductListHeader>

        <ProductList>
          {productPackageProducts.map(({ product }) => (
            <ProductPackageCard
              key={product!.id}
              product={product!}
              isProductFilled={isProductFilled(product!.id)}
              isSelected={product!.id === selectedProductId}
              selectedDate={getSelectedDate(product!.id)}
              selectedHour={getSelectedTime(product!.id)}
              selectedTariffs={getSelectedTariffs(product!.id)}
              onClick={handleSelectProduct}
              disabled={hasInactiveProduct || shouldProductBeDisabled(product!.id)}
            />
          ))}
        </ProductList>
      </ProductListScrollableContent>

      <ProductListFooter>
        <ProductListFooterInfo>
          <span>{i18n('product_package_adder_total')}: </span>
          <strong>{formatCents(packageTotalCents)}</strong>
        </ProductListFooterInfo>
      </ProductListFooter>
    </ProductListArea>
  );
};

export default ProductPackageAdderProductList;
